import React, { useState } from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import {
  LearnerArticle,
  LearnerContentList,
  LearnerRecording,
  LearnerGroupSession,
  LearnerPractitioner,
  LearnerPrivateSession,
} from "apps/user/learner/api/types";
import { toKebabCase } from "utility/Function";

import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import StarIcon from "Assets/holidaysFireworksStar.svg";
import ZigZagIcon from "Assets/icon/biometrics.svg";
import ForYouIcon from "Assets/icon/forYou.svg";
import TrendingNowIcon from "Assets/icon/mostPopular.svg";
import ContinuePractisingIcon from "Assets/icon/continuePractising.svg";
import Card from "./card";
import styles from "./preview.module.scss";

interface HeadingProps {
  contentListId: string;
  kebabCaseId: string;
  seeMore?: boolean;
  icon?: string;
  label: string;
  descriptionHtml?: string;
  hide?: boolean;
  featured?: boolean;
  biometrics?: boolean;
}

const Heading: React.FC<HeadingProps> = ({
  contentListId,
  kebabCaseId,
  seeMore,
  icon,
  label,
  descriptionHtml,
  hide,
  featured,
  biometrics,
}) => {
  // Set default icon for featured lists
  if (featured) {
    icon = StarIcon;
  }
  if (biometrics) {
    icon = ZigZagIcon;
  }

  switch (contentListId) {
    case "LearnerHomeForYou":
      icon = ForYouIcon;
      break;
    case "LearnerHomeTrendingNow":
      icon = TrendingNowIcon;
      break;
    case "LearnerHomeContinuePractising":
      icon = ContinuePractisingIcon;
      break;
    default:
      break;
  }

  if (hide) {
    return null;
  }

  const heading = (
    <div className={styles["heading"]}>
      {icon && <img src={icon} alt={"icon"} />}
      <h2 className={styles["label"]}>{label}</h2>
      {(featured || biometrics) && <span className={styles["line"]} />}
      {seeMore && <ChevronIcon className={styles["arrow-right"]} />}
    </div>
  );

  const description = descriptionHtml ? (
    <p
      className={styles["description"]}
      dangerouslySetInnerHTML={{ __html: descriptionHtml }}
    />
  ) : (
    <div className={styles["mb"]} />
  );

  if (seeMore) {
    if (kebabCaseId.includes("on-demand")) {
      return (
        <Link
          to={`content-lists/${kebabCaseId}`}
          state={{ id: contentListId, pageTitlePrefix: label, canGoBack: true }}
        >
          {heading}
          {description}
        </Link>
      );
    }
    return (
      <Link
        to={`content-lists/${kebabCaseId}`}
        state={{ id: contentListId, pageTitlePrefix: label, canGoBack: true }}
      >
        {heading}
        {description}
      </Link>
    );
  }

  return (
    <>
      {heading}
      {description}
    </>
  );
};

interface ListProps {
  name: string;
  items: (
    | LearnerArticle
    | LearnerRecording
    | LearnerGroupSession
    | LearnerPrivateSession
    | LearnerPractitioner
  )[];
  limit?: number;
  onDeleteClick?: (deleteParams: DeleteParams) => void;
  isLargeCard?: boolean;
  isReplayCard?: boolean;
}

export type DeleteParams = {
  itemId: string;
  typename: string;
  contentListId: string;
};

const List: React.FC<ListProps> = ({
  name,
  items,
  limit,
  onDeleteClick,
  isLargeCard,
  isReplayCard,
}: ListProps) => {
  const [removedId, setIsRemovedId] = useState<string>("");

  if (items.length === 0) {
    return null;
  }

  items = limit ? items.slice(0, limit) : items;

  return (
    <ul className={clsx("row g-3", styles["items"])}>
      {items.map((item, index) => (
        <li
          className={clsx(
            "col-md-12",
            "col-lg-6",
            (!limit || limit > 2) && "col-xl-4",
            removedId === item.id && styles["disappear"]
          )}
          key={item.id}
        >
          <Card
            item={item}
            listName={name}
            listPosition={index + 1}
            showBiometrics={name === "LearnerBiometricsSessionsHistory"}
            onDeleteClick={(deleteParams: DeleteParams) => {
              setIsRemovedId(deleteParams.itemId);
              onDeleteClick(deleteParams);
            }}
            isLargeCard={isLargeCard}
            isReplayCard={isReplayCard}
          />
        </li>
      ))}
    </ul>
  );
};

interface ContentListPreviewProps {
  data: LearnerContentList;
  seeMore?: boolean;
  hideHeading?: boolean;
  limit?: number;
  icon?: string;
  featured?: boolean;
  biometrics?: boolean;
  onDeleteClick?: (deleteParams: DeleteParams) => void;
  isLargeCard?: boolean;
  isReplayCard?: boolean;
}

export const ContentListPreview: React.FC<ContentListPreviewProps> = ({
  data,
  seeMore,
  hideHeading,
  icon,
  featured,
  biometrics,
  onDeleteClick,
  isLargeCard,
  isReplayCard,
}) => {
  const { __typename, id, label, descriptionHtml, items } = data;

  // Don't render if there are no items
  if (items.length === 0) {
    return null;
  }

  const kebabCaseId = toKebabCase(id);

  return (
    <section id={kebabCaseId} className={styles["container"]}>
      <Heading
        contentListId={id}
        kebabCaseId={kebabCaseId}
        seeMore={seeMore}
        icon={icon}
        label={label}
        descriptionHtml={descriptionHtml}
        hide={hideHeading}
        featured={featured ?? __typename.startsWith("LearnerFeatured")}
        biometrics={
          biometrics ?? __typename.startsWith("LearnerIndexBiometrics")
        }
      />
      <List
        name={id}
        items={items}
        onDeleteClick={onDeleteClick}
        isLargeCard={isLargeCard}
        isReplayCard={isReplayCard}
      />
    </section>
  );
};

export default ContentListPreview;
